import React, { useState } from 'react';
import './BookingForm.css';

const BookingForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    date: '',
    time: '',
    guests: '',
    message: '',
    gdprConsent: false
  });

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  // ❗ Evening fully booked (ONLY for 13 December 2025)
  const eveningBlockedDates = ['2025-12-13']; // <-- 10th removed!

  // Evening times to block
  const eveningBlockedTimes = [
    '17:00', '17:15', '17:30', '17:45',
    '18:00', '18:15', '18:30', '18:45',
    '19:00', '19:15', '19:30', '19:45',
    '20:00', '20:15', '20:30', '20:45',
    '21:00'
  ];

  // Generate time slots
  const generateTimeSlots = () => {
    const times = [];

    const addSlots = (startHour, endHour) => {
      const start = startHour * 60;
      const end = endHour * 60;
      for (let i = start; i <= end; i += 15) {
        const hours = Math.floor(i / 60);
        const minutes = i % 60;
        const formatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        times.push(formatted);
      }
    };

    addSlots(11, 14); // lunch
    addSlots(17, 21); // dinner

    return times;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'guests' && parseInt(value) > 9) {
      setModalMessage('Für Gruppen von mehr als 9 Personen rufen Sie uns bitte direkt an.');
      setShowModal(true);
      return;
    }

    if (name === 'date' && value) {
      const selectedDate = new Date(value);
      const isTargetDate = value === '2026-09-01' || (
        !isNaN(selectedDate.getTime()) &&
        selectedDate.getFullYear() === 2026 &&
        selectedDate.getMonth() === 8 && // September is index 8
        selectedDate.getDate() === 1
      );

      if (isTargetDate) {
        setModalMessage('Entschuldigung, das Restaurant ist am 01.09.2026 geschlossen. Reservierungen sind an diesem Tag leider nicht möglich.');
        setShowModal(true);
        setFormData(prev => ({
          ...prev,
          date: ''
        }));
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const isMonday = (dateString) => {
    const date = new Date(dateString);
    return date.getDay() === 1;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim())
      newErrors.name = 'Bitte füllen Sie dieses Feld aus';

    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';

    if (!formData.mobile.trim())
      newErrors.mobile = 'Bitte geben Sie Ihre Telefonnummer ein';
    else if (!/^\+?[0-9]{10,15}$/.test(formData.mobile))
      newErrors.mobile = 'Bitte geben Sie eine gültige Telefonnummer ein';

    if (!formData.date)
      newErrors.date = 'Bitte wählen Sie ein Datum aus';
    else if (formData.date === '2026-09-01')
      newErrors.date = 'Entschuldigung, das Restaurant ist am 01.09.2026 geschlossen.';
    else if (isMonday(formData.date))
      newErrors.date = 'Entschuldigung, am Montag haben wir geschlossen';

    // Evening block validation (ONLY for 13 December)
    if (
      formData.time &&
      eveningBlockedDates.includes(formData.date) &&
      eveningBlockedTimes.includes(formData.time)
    ) {
      newErrors.time = 'Dieser Zeitraum ist an diesem Tag vollständig reserviert.';
    }

    if (!formData.time)
      newErrors.time = 'Bitte wählen Sie eine Uhrzeit aus';

    if (!formData.guests)
      newErrors.guests = 'Bitte geben Sie die Anzahl der Personen an';

    if (!formData.gdprConsent)
      newErrors.gdprConsent = 'Bitte bestätigen Sie die Datenschutzerklärung';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    const BASE_URL =
      window.location.hostname === 'localhost'
        ? 'http://localhost:8080'
        : 'https://app.papadam-pfullendorf.de';

    try {
      const response = await fetch(`${BASE_URL}/api/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const errorData = await response.json();
        alert('Fehler beim Absenden der Reservierung: ' + (errorData.message || response.statusText));
      }
    } catch (error) {
      alert('Netzwerkfehler oder Backend nicht erreichbar.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="booking-success">
        <div className="success-icon">✓</div>
        <h2>Reservierungsanfrage erhalten!</h2>
        <p>
          Vielen Dank, {formData.name}! Ihre Reservierungsanfrage für {formData.guests}{' '}
          {formData.guests === '1' ? 'Person' : 'Personen'} am {formData.date} um {formData.time} Uhr wurde erhalten.
          Wir werden Ihre Reservierung schriftlich bestätigen.
        </p>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <div className="hero-image">
        <h1 className="hero-title">Machen Sie eine Reservierung</h1>
      </div>

      <div className="subheading">
        <h2>Ihr verbindlich freier Platz</h2>
        <hr />
      </div>

      <form className="booking-form" onSubmit={handleSubmit}>

        <label>Name:</label>
        <input type="text" name="name" value={formData.name} onChange={handleChange} />
        {errors.name && <span className="error">{errors.name}</span>}

        <label>E-Mail:</label>
        <input type="email" name="email" value={formData.email} onChange={handleChange} />
        {errors.email && <span className="error">{errors.email}</span>}

        <label>Telefonnummer:</label>
        <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} placeholder="+491234567890" />
        {errors.mobile && <span className="error">{errors.mobile}</span>}

        <label>Gewünschtes Reservierungsdatum:</label>
        <input
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          min={new Date().toISOString().split('T')[0]}
        />
        {errors.date && <span className="error">{errors.date}</span>}

        {/* ❗ SHOW custom evening fully booked message – ONLY for 13 Dec */}
        {eveningBlockedDates.includes(formData.date) && (
          <div className="error" style={{ marginTop: '5px' }}>
            Abendzeiten (17:00–22:00) sind an diesem Tag vollständig ausgebucht.
          </div>
        )}

        <label>Gewünschte Uhrzeit:</label>
        <select name="time" value={formData.time} onChange={handleChange}>
          <option value="">- Bitte auswählen -</option>

          {generateTimeSlots().map(t => (
            <option
              key={t}
              value={t}
              disabled={
                eveningBlockedDates.includes(formData.date) &&
                eveningBlockedTimes.includes(t)
              }
            >
              {t} Uhr
              {(eveningBlockedDates.includes(formData.date) &&
                eveningBlockedTimes.includes(t)) && ' (ausgebucht)'}
            </option>
          ))}
        </select>
        {errors.time && <span className="error">{errors.time}</span>}

        <label>Anzahl der Personen:</label>
        <select name="guests" value={formData.guests} onChange={handleChange}>
          <option value="">- Bitte auswählen -</option>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
            <option key={n} value={n}>
              {n} {n === 1 ? 'Person' : 'Personen'}
            </option>
          ))}
        </select>
        {errors.guests && <span className="error">{errors.guests}</span>}

        <label>Zusätzliche Nachricht:</label>
        <textarea name="message" value={formData.message} onChange={handleChange} />

        <label className="gdpr-consent">
          <input
            type="checkbox"
            name="gdprConsent"
            checked={formData.gdprConsent}
            onChange={handleChange}
          />
          Ich stimme zu, dass diese Website meine eingegebenen Daten speichert.
        </label>
        {errors.gdprConsent && <span className="error">{errors.gdprConsent}</span>}

        <button type="submit" disabled={loading}>
          {loading ? 'Senden...' : 'ABSENDEN'}
        </button>
      </form>

      {showModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal-content">
            <h3>Hinweis</h3>
            <p>{modalMessage}</p>
            <button type="button" className="custom-modal-close" onClick={() => setShowModal(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingForm;
